import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import publicApi from '../services/publicApi';

const API = publicApi;

const COLORS = {
  background: '#0F1923',
  card: '#1A2B3C',
  blue: '#4A7EB5',
  textPrimary: '#FFFFFF',
  textSecondary: '#8FA8C0',
  border: '#2D4259',
  success: '#10B981',
  warning: '#F59E0B',
};

const TAMKO_COLOR_MAP: Record<string, string> = {
  'Weathered Wood': '#7D6A52',
  'Rustic Cedar': '#8B5E3C',
  'Rustic Redwood': '#7A3B2E',
  'Rustic Slate': '#6B7280',
  'Rustic Black': '#2D2D2D',
  'Natural Timber': '#9C7E5F',
  'Charcoal': '#4B5563',
  'Heritage Brown': '#5C3D2E',
};

type ContractData = {
  homeowner_name: string;
  address: string;
  claim_number: string;
  insurance_company: string;
  shingle_color: string;
  shingle_line: string;
  drip_edge_color?: string;
  payment_arrangement?: string;
  customer_responsibility?: number;
  carrier_contribution?: number;
  rcv: number;
  deductible: number;
  roofing_upgrades: string[];
  roofing_upsells: string[];
  roofing_upgrade_prices?: Record<string, number>;
  roofing_upsell_prices?: Record<string, number>;
  warranty_tier?: string;
  warranty_50yr: boolean;
  specialist_name: string;
  specialist_phone: string;
};

const money = (v: number) =>
  v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });

export default function ContractSignPage() {
  const { token } = useParams<{ token: string }>();
  const sigRef = useRef<SignatureCanvas>(null);

  const [data, setData] = useState<ContractData | null>(null);
  const [loading, setLoading] = useState(true);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [assignedBenefits, setAssignedBenefits] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    API.get(`/public/contract/${token}`)
      .then((res) => {
        if (res.data.already_signed) {
          setAlreadySigned(true);
          return;
        }
        setData(res.data);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!agreed) return setError('Please agree to the contract terms.');
    if (data?.payment_arrangement !== 'retail' && !assignedBenefits) return setError('Please acknowledge assignment of benefits.');
    if (!hasSig || sigRef.current?.isEmpty()) return setError('Please sign before submitting.');

    setError('');
    setSubmitting(true);
    try {
      await API.post(`/public/contract/${token}`, {
        signature_image: sigRef.current?.toDataURL('image/png'),
        agreed: true,
      });
      setDone(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to sign contract.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={styles.page}><div style={styles.card}><div style={styles.centerText}>Loading contract…</div></div></div>;
  if (notFound) return <div style={styles.page}><div style={styles.card}><div style={styles.centerText}>Contract link not found.</div></div></div>;
  if (alreadySigned) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.successCard}>✅ This contract has already been signed. Thank you!</div>
      </div>
    </div>
  );
  if (done) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.successCard}>✅ Contract signed successfully. Your project is moving forward.</div>
      </div>
    </div>
  );
  if (!data) return null;

  const shingleColorHex = TAMKO_COLOR_MAP[data.shingle_color] || COLORS.border;

  return (
    <div style={styles.page}>
      <div style={styles.headerBar}>
        <img
          src="/logo-dark.png"
          alt="McMullen Browne"
          style={{ height: 36 }}
        />
        <div>
          <div style={styles.headerTitle}>Roofing Restoration Agreement</div>
          <div style={styles.headerSub}>McMullen Browne Development LLC</div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.infoCard}>
          <h2 style={styles.h2}>{data.homeowner_name}</h2>
          <p style={styles.p}>{data.address}</p>
        </div>

        <div style={styles.infoCard}>
          <div style={styles.twoCol}>
            <div>
              <div style={styles.label}>Insurance Company</div>
              <div style={styles.value}>{data.insurance_company || '—'}</div>
              <div style={styles.label}>Claim #</div>
              <div style={styles.value}>{data.claim_number || '—'}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={styles.label}>{data.payment_arrangement && data.payment_arrangement !== 'insurance' ? 'Agreed Contract Price' : 'Approved RCV'}</div>
              <div style={styles.bigMoney}>{money(data.rcv || 0)}</div>
              <div style={styles.label}>{data.payment_arrangement && data.payment_arrangement !== 'insurance' ? 'Customer Responsibility' : 'Deductible'}</div>
              <div style={styles.value}>{money(data.payment_arrangement && data.payment_arrangement !== 'insurance' ? data.customer_responsibility || 0 : data.deductible || 0)}</div>
            </div>
          </div>
        </div>

        <div style={styles.infoCard}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 42, height: 42, borderRadius: 21, background: shingleColorHex, border: `1px solid ${COLORS.border}` }} />
            <div>
              <div style={styles.value}>TAMKO Titan XT — {data.shingle_color || 'TBD'}</div>
              <div style={styles.p}>130 MPH wind · Class 4 impact · Lifetime base warranty</div>
            </div>
          </div>
        </div>

        {Array.isArray(data.roofing_upgrades) && data.roofing_upgrades.length > 0 ? (
          <div style={styles.infoCard}>
            <div style={styles.sectionTitle}>Selected Upgrades</div>
            {data.roofing_upgrades.map((item) => {
              const price = Number(data.roofing_upgrade_prices?.[item] || 0);
              return (
                <div key={item} style={{ ...styles.li, display: 'flex', justifyContent: 'space-between' }}>
                  <span>✓ {String(item).replace(/_/g, ' ')}</span>
                  {price > 0 ? <span style={{ color: COLORS.textSecondary }}>{money(price)}</span> : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {Array.isArray(data.roofing_upsells) && data.roofing_upsells.length > 0 ? (
          <div style={styles.infoCard}>
            <div style={styles.sectionTitle}>Add-Ons</div>
            {data.roofing_upsells.map((item) => {
              const price = Number(data.roofing_upsell_prices?.[item] || 0);
              return (
                <div key={item} style={{ ...styles.li, display: 'flex', justifyContent: 'space-between' }}>
                  <span>✓ {String(item).replace(/_/g, ' ')}</span>
                  {price > 0 ? <span style={{ color: COLORS.textSecondary }}>{money(price)}</span> : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {(() => {
          const tier = data.warranty_tier || (data.warranty_50yr ? 'platinum' : 'base');
          const tierBanners: Record<string, string> = {
            gold: '⭐ Gold Warranty — 20-Year Material Coverage',
            platinum: '💎 Platinum Warranty — 50-Year Material Coverage',
            master_shield: '🏆 Master Shield Warranty — 50-Year Material + 10-Year Workmanship',
          };
          const banner = tierBanners[tier];
          if (!banner) return null;
          return (
            <div style={{ ...styles.warningBanner, background: COLORS.warning + '22', borderColor: COLORS.warning }}>
              {banner} included
            </div>
          );
        })()}

        {(() => {
          const tier = data.warranty_tier || (data.warranty_50yr ? 'platinum' : 'base');
          const tierShort: Record<string, string> = {
            base: '10yr Base + 3yr Workmanship',
            gold: '20yr Gold + 3yr Workmanship',
            platinum: '50yr Platinum + 3yr Workmanship',
            master_shield: '50yr Master Shield + 10yr Workmanship',
          };
          return (
            <div style={styles.termGrid}>
              <div style={styles.termCard}><div style={styles.label}>Expected Insurer Contribution</div><div style={styles.value}>{money(data.payment_arrangement && data.payment_arrangement !== 'insurance' ? data.carrier_contribution || 0 : Math.max(0,(data.rcv||0)-(data.deductible||0)))}</div></div>
              <div style={styles.termCard}><div style={styles.label}>{data.payment_arrangement && data.payment_arrangement !== 'insurance' ? 'Customer Responsibility' : 'Your Deductible'}</div><div style={styles.value}>{money(data.payment_arrangement && data.payment_arrangement !== 'insurance' ? data.customer_responsibility || 0 : data.deductible || 0)}</div></div>
              <div style={styles.termCard}><div style={styles.label}>Warranty</div><div style={styles.value}>{tierShort[tier] || tierShort.base}</div></div>
            </div>
          );
        })()}

        {data.payment_arrangement !== 'retail' && <>        <label style={styles.checkboxRow}>
          <input type="checkbox" checked={assignedBenefits} onChange={(e) => setAssignedBenefits(e.target.checked)} style={styles.checkbox} />
          <span style={styles.p}>I authorize McMullen Browne Development LLC to receive my insurance proceeds and file supplements on my behalf.</span>
        </label></>}

        <label style={styles.checkboxRow}>
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={styles.checkbox} />
          <span style={styles.p}>I have reviewed this Roofing Restoration Agreement and agree to its terms.</span>
        </label>

        <div style={styles.infoCard}>
          <div style={styles.sectionTitle}>Signature</div>
          <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
            <SignatureCanvas
              ref={sigRef}
              penColor="#0F1923"
              canvasProps={{ style: { width: '100%', height: 200, display: 'block' } }}
              onEnd={() => setHasSig(true)}
            />
          </div>
          {hasSig ? (
            <button onClick={() => { sigRef.current?.clear(); setHasSig(false); }} style={styles.clearBtn}>Clear signature</button>
          ) : null}
        </div>

        {error ? <div style={styles.errorBox}>{error}</div> : null}

        <button onClick={submit} disabled={submitting} style={styles.submitBtn}>
          {submitting ? 'Submitting…' : 'Sign Roofing Contract'}
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: COLORS.background,
    padding: '16px',
  },
  headerBar: {
    maxWidth: 840,
    margin: '0 auto 12px',
    background: COLORS.blue,
    borderRadius: 12,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerTitle: { color: COLORS.textPrimary, fontWeight: 800, fontSize: 18 },
  headerSub: { color: COLORS.textPrimary, opacity: 0.85, fontSize: 12 },
  card: {
    maxWidth: 840,
    margin: '0 auto',
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 16,
  },
  centerText: { color: COLORS.textSecondary, textAlign: 'center', padding: '50px 0' },
  infoCard: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
    background: COLORS.background,
  },
  h2: { color: COLORS.textPrimary, margin: 0, fontSize: 22 },
  p: { color: COLORS.textSecondary, margin: 0, fontSize: 13, lineHeight: 1.6 },
  label: { color: COLORS.textSecondary, fontSize: 11, textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 },
  value: { color: COLORS.textPrimary, fontSize: 14, fontWeight: 700, marginBottom: 8 },
  bigMoney: { color: COLORS.blue, fontWeight: 900, fontSize: 28, marginBottom: 10 },
  sectionTitle: { color: COLORS.textPrimary, fontSize: 15, fontWeight: 800, marginBottom: 8 },
  li: { color: COLORS.success, fontSize: 13, marginBottom: 6 },
  warningBanner: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 10,
    color: COLORS.warning,
    fontWeight: 700,
    padding: 10,
    marginBottom: 12,
  },
  termGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: 10,
    marginBottom: 12,
  },
  termCard: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: 10,
    background: COLORS.background,
  },
  checkboxRow: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  checkbox: { marginTop: 3, accentColor: COLORS.blue },
  clearBtn: {
    marginTop: 8,
    border: 'none',
    padding: 0,
    background: 'none',
    color: COLORS.warning,
    fontWeight: 700,
    cursor: 'pointer',
  },
  errorBox: {
    background: COLORS.warning + '22',
    border: `1px solid ${COLORS.warning}`,
    color: COLORS.warning,
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 10,
    fontSize: 13,
  },
  submitBtn: {
    width: '100%',
    padding: '16px 18px',
    border: 'none',
    borderRadius: 10,
    background: COLORS.blue,
    color: COLORS.textPrimary,
    fontWeight: 800,
    fontSize: 16,
    cursor: 'pointer',
  },
  successCard: {
    background: COLORS.success + '22',
    border: `1px solid ${COLORS.success}`,
    color: COLORS.success,
    borderRadius: 10,
    padding: '14px 16px',
    fontWeight: 800,
  },
  twoCol: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
  },
};
